import React, { useState } from 'react';
import { addWarmupArticle } from '../../services/WarmupArticleServiceFirestore';
import { Liga } from '../../models/types';
import { getExportedImages, ExportedImage } from '../../services/thumbnailService';
import { useAuth } from '../../context/AuthContext';
import styled from 'styled-components';

interface Props {
  promptsGen: { result: string }[];
  liga: Liga | undefined;
  circuito: string;
}

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 5px;
  width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`;

const ImageGrid = styled.div`
  margin-top: 15px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 15px;
`;

const ImageCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s;
  &:hover {
    transform: translateY(-5px);
  }
`;

const ImagePreview = styled.div`
  height: 150px;
  overflow: hidden;
  position: relative;
`;

const ImageInfo = styled.div`
  padding: 10px;
`;

const ImageTitle = styled.div`
  margin-bottom: 5px;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ImageDate = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 10px;
`;

const SelectButton = styled.button`
  width: 100%;
  padding: 8px 0;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  &:hover {
    background-color: #2980b9;
  }
`;

const CoverImageContainer = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CoverImagePreview = styled.div`
  width: 100%;
  max-height: 150px;
  overflow: hidden;
  margin-bottom: 10px;
  border-radius: 4px;
  img {
    width: 100%;
    height: auto;
    object-fit: cover;
  }
`;

const PublicarWarmUpButton: React.FC<Props> = ({ promptsGen, liga, circuito }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [exportedImages, setExportedImages] = useState<ExportedImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedCoverImage, setSelectedCoverImage] = useState<ExportedImage | null>(null);

  // Cargar imágenes exportadas del usuario actual
  const loadExportedImages = async () => {
    if (!currentUser?.uid) return;
    
    setLoadingImages(true);
    try {
      const images = await getExportedImages(currentUser.uid);
      setExportedImages(images);
    } catch (err) {
      console.error('Error al cargar imágenes:', err);
      setError('No se pudieron cargar las imágenes guardadas');
    } finally {
      setLoadingImages(false);
    }
  };

  // Abrir modal de selección de imagen
  const handleOpenImageModal = () => {
    setShowImageModal(true);
    loadExportedImages();
  };

  // Seleccionar imagen como portada
  const handleSelectCoverImage = (image: ExportedImage) => {
    setSelectedCoverImage(image);
    setShowImageModal(false);
  };

  // Eliminar imagen de portada seleccionada
  const handleRemoveCoverImage = () => {
    setSelectedCoverImage(null);
  };

  const handlePublicar = async () => {
    setLoading(true);
    setError('');
    setSuccess(false);
    try {
      const contenidoMarkdown = promptsGen.map(pg => pg.result).filter(Boolean).join('\n\n---\n\n');

      // 1. Generar título periodístico/deportivo con IA
      let titulo = '';
      try {
        const promptTitulo = `Liga: ${liga?.nombre || ''}\nCircuito: ${circuito}\n\nRedacta SOLO un título periodístico, creativo, deportivo y emocionante para un artículo previo de simracing sobre este circuito y esta liga.\nNo añadas subtítulos, ni explicaciones, ni hashtags, ni comillas. Solo el título. Ejemplos de estilo: 'Noche de leyendas en Marina Bay', 'Duelo bajo las luces: la batalla de Singapur', 'Expectación máxima en la Liga Primavera Pro'.`;
        const res = await fetch('https://api.deepseek.com/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_DEEPSEEK_API_KEY}`,
          },
          body: JSON.stringify({
            model: 'deepseek-chat',
            messages: [
              { role: 'system', content: 'Eres un redactor profesional de simracing. Genera SOLO títulos periodísticos/deportivos, deportivos y emocionantes.' },
              { role: 'user', content: promptTitulo }
            ],
            stream: false
          })
        });
        if (!res.ok) throw new Error('Error al generar el título con IA');
        const data = await res.json();
        titulo = (data.choices?.[0]?.message?.content || '').trim();
        if (!titulo) throw new Error('La IA no devolvió título');
      } catch (err: any) {
        // Fallback clásico
        titulo = `${circuito} (${liga?.nombre || ''})`;
        setError('No se pudo generar un título creativo con IA. Se usará uno clásico.');
      }

      await addWarmupArticle({
        ligaId: liga?.id || '',
        ligaNombre: liga?.nombre || '',
        circuito,
        fecha: new Date().toISOString(),
        autor: 'Redacción',
        contenidoMarkdown,
        createdAt: Date.now(),
        titulo,
        coverImageUrl: selectedCoverImage?.imageUrl, // Añadir URL de la imagen de portada si está seleccionada
      });
      setSuccess(true);
      setSelectedCoverImage(null); // Limpiar la imagen seleccionada después de publicar
    } catch (e: any) {
      setError(e.message || 'Error al publicar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{marginBottom:16}}>
      {/* Mostrar la imagen seleccionada si existe */}
      {selectedCoverImage && (
        <CoverImageContainer>
          <CoverImagePreview>
            <img src={selectedCoverImage.imageUrl} alt="Imagen de portada" />
          </CoverImagePreview>
          <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
            <button 
              onClick={handleRemoveCoverImage} 
              style={{flex: 1, padding:'6px 12px', borderRadius:4, background:'#e74c3c', color:'#fff', border:'none', fontWeight:'bold', cursor:'pointer'}}
            >
              Eliminar Imagen
            </button>
            <button 
              onClick={handleOpenImageModal} 
              style={{flex: 1, padding:'6px 12px', borderRadius:4, background:'#3498db', color:'#fff', border:'none', fontWeight:'bold', cursor:'pointer'}}
            >
              Cambiar Imagen
            </button>
          </div>
        </CoverImageContainer>
      )}
      
      {/* Botones en fila */}
      <div style={{display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap'}}>
        {/* Botón de seleccionar imagen (solo si no hay imagen seleccionada) */}
        {!selectedCoverImage && (
          <button 
            onClick={handleOpenImageModal} 
            style={{padding:'8px 18px', borderRadius:25, background:'#3498db', color:'#fff', border:'none', fontWeight:'bold', cursor:'pointer', boxShadow: '0 4px 15px rgba(52, 152, 219, 0.3)'}}
          >
            🖼️ Seleccionar Imagen
          </button>
        )}

        {/* Botón de publicar */}
        <button
          onClick={handlePublicar}
          disabled={loading}
          style={{padding:'8px 18px', borderRadius:25, background:'#e4312e', color:'#fff', border:'none', fontWeight:'bold', cursor: loading ? 'not-allowed' : 'pointer', boxShadow: '0 4px 15px rgba(228, 49, 46, 0.3)'}}
        >{loading ? 'Publicando...' : 'Publicar en WarmUp'}</button>
        
        {success && <span style={{color:'#27ae60', fontWeight:'bold'}}>¡Publicado!</span>}
        {error && <span style={{color:'#e74c3c', fontWeight:'bold'}}>Error: {error}</span>}
      </div>

      {/* Modal de selección de imagen */}
      {showImageModal && (
        <Modal>
          <ModalContent>
            <h3>Mis Imágenes Guardadas</h3>
            <p>Selecciona una imagen para usar como portada de tu artículo.</p>
            
            <button 
              onClick={() => setShowImageModal(false)} 
              style={{marginBottom: '15px', padding:'6px 12px', borderRadius:4, background:'#7f8c8d', color:'#fff', border:'none', fontWeight:'bold', cursor:'pointer'}}
            >
              Cancelar
            </button>
            
            {loadingImages ? (
              <p>Cargando imágenes...</p>
            ) : exportedImages.length === 0 ? (
              <p>No tienes imágenes guardadas. Puedes crear imágenes en el Generador de Miniaturas.</p>
            ) : (
              <ImageGrid>
                {exportedImages.map((image) => (
                  <ImageCard key={image.id} onClick={() => handleSelectCoverImage(image)}>
                    <ImagePreview>
                      <img 
                        src={image.imageUrl} 
                        alt={image.name} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                      />
                    </ImagePreview>
                    <ImageInfo>
                      <ImageTitle>{image.name}</ImageTitle>
                      <ImageDate>
                        {image.createdAt ? new Date((image.createdAt as any).seconds * 1000).toLocaleDateString() : 'Fecha desconocida'}
                      </ImageDate>
                      <SelectButton>Seleccionar</SelectButton>
                    </ImageInfo>
                  </ImageCard>
                ))}
              </ImageGrid>
            )}
          </ModalContent>
        </Modal>
      )}
    </div>
  );
};

export default PublicarWarmUpButton;
